import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ArrowLeft, Loader2, Gamepad2, Download } from 'lucide-react';

function fmtDateTime(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; }
}

function downloadCSV(rows, columns, filename) {
    const header = columns.map((c) => c.label).join(',') + '\n';
    const body = rows.map((r) => columns.map((c) => `"${String(c.render ? c.render(r) : (r[c.key] ?? '')).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

export default function AdminGameBreakdown() {
    const { gameCode } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');

    useEffect(() => {
        setData(null); setErr('');
        api.get(`/admin/reports/game/${gameCode}/users`)
            .then((r) => setData(r.data))
            .catch(() => setErr('Failed to load game breakdown'));
    }, [gameCode]);

    const rows = data?.rows || [];
    const columns = [
        { key: 'full_name', label: 'Learner' },
        { key: 'department', label: 'Department' },
        { key: 'region', label: 'Region' },
        { key: 'plays', label: 'Plays', align: 'right' },
        { key: 'best_score', label: 'Best Score', align: 'right' },
        { key: 'points_earned', label: 'Points', align: 'right' },
        { key: 'last_played', label: 'Last Played', align: 'right', render: (r) => fmtDateTime(r.last_played) },
    ];

    return (
        <div data-testid="admin-game-breakdown-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4" data-testid="game-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back to reports
            </button>

            <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-blue/10 text-brand-blue"><Gamepad2 className="h-6 w-6" /></span>
                <div>
                    <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900" data-testid="game-name">{data?.game_name || gameCode}</h1>
                    <p className="text-sm text-slate-500">Per-learner breakdown · {rows.length} player{rows.length === 1 ? '' : 's'}</p>
                </div>
            </div>

            {err && <div className="p-6 text-sm text-rose-600 bg-white rounded-2xl border border-slate-200">{err}</div>}
            {!data && !err && <div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

            {data && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="game-breakdown-table">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-600">{rows.length} record{rows.length === 1 ? '' : 's'}</div>
                        <button onClick={() => downloadCSV(rows, columns, `${gameCode}-players.csv`)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-black text-brand-yellow text-xs font-semibold" data-testid="game-export-btn">
                            <Download className="h-3.5 w-3.5" /> Export CSV
                        </button>
                    </div>
                    <div className="overflow-auto max-h-[65vh]">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-600">
                                <tr>{columns.map((c) => <th key={c.key} className={`py-3 px-4 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`game-player-${r.user_id}`}>
                                        {columns.map((c) => <td key={c.key} className={`py-2.5 px-4 ${c.align === 'right' ? 'text-right' : ''}`}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
                                    </tr>
                                ))}
                                {!rows.length && <tr><td colSpan={columns.length} className="py-12 text-center text-sm text-slate-500">No players yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
